import { inject, NgZone } from '@angular/core';
import { filter, map, Observable, of, Subject, switchMap, timer } from 'rxjs';

export const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**

 * It converts an epoch to a date string

 * @param dateStr epoch

 * @param locale locale string e.g. 'nl-NL'

 * @param options date time format options

 * @returns an stringifyed date

 * @author Favioz

 */

export function getDateName(
  dateStr: number,
  locale: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
) {
  const date = new Date(dateStr);

  return date.toLocaleDateString(locale, options);
}

/**

 * @date epoch date

 * @returns boolean, true if date is in the future

 */

export function isDateInTheFuture(date: number): boolean {
  return Date.now() < date;
}

/**

 *

 * @param date epoch date

 * @returns boolean, true if date is in the past

 */

export function isDateInthePast(date: number): boolean {
  return Date.now() > date;
}

/**

 *

 * @param from epoch from date

 * @param to epoch to date

 * @returns boolean, true if date range is in the present from < now < to

 */

export function isDateRangePresent(from: number, to: number): boolean {
  return from < Date.now() && Date.now() < to;
}

/**

 *

 * @param from epoch from date

 * @param to epoch to date

 * @returns boolean, true if date range is in the past

 */

export function isDateRangePast(from: number, to: number): boolean {
  return from < Date.now() && to < Date.now();
}

/**

 *

 * @param from epoch from date

 * @param to epoch to date

 * @returns boolean, true if date range is in the future

 */

export function isDateRangeFuture(from: number, to: number): boolean {
  return from > Date.now() && to > Date.now();
}

/**

 * Transforms a date string in the format dd-mm-yyyy to a Date object

 * @param date string date in the format dd-mm-yyyy

 * @returns

 */

export function getDateFromDDMMYYYY(date: string): Date {
  if (!date) return null;

  const splittedDate = date.split('-');

  return new Date(`${splittedDate[2]}/${splittedDate[1]}/${splittedDate[0]}`);
}

/**

 * Takes a date string as input in the format dd-mm-yyyy and returns string Today/Tomorrow/False accordingly

 * @param date string date in the format dd-mm-yyyy

 * @returns

 */

export function isTodayOrTomorrow(dateString: string): string | boolean {
  const today = new Date();
  const tomorrow = new Date();
  const incomingDate = new Date(dateString); // Incoming date in local time
  // Convert everything to UTC for accurate comparison
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const tomorrowUTC = new Date(Date.UTC(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth(), tomorrow.getUTCDate()));
  tomorrowUTC.setUTCDate(todayUTC.getUTCDate() + 1);
  const incomingUTC = new Date(
    Date.UTC(incomingDate.getUTCFullYear(), incomingDate.getUTCMonth(), incomingDate.getUTCDate())
  );
  if (incomingUTC.getTime() === todayUTC.getTime()) return 'today';
  else if (incomingUTC.getTime() === tomorrowUTC.getTime()) return 'tomorrow';
  else return false;
}
// Helper function to parse date from input format

function parseDate(dateString: string, format: string) {
  const parts = dateString.split(/[^0-9]+/);

  const formatParts = format.match(/(dd|mm|yyyy)/g);

  const dateInfo = { day: 0, month: 0, year: 0 };

  formatParts.forEach((part, index) => {
    if (part === 'dd') dateInfo.day = parseInt(parts[index], 10);
    else if (part === 'mm')
      dateInfo.month = parseInt(parts[index], 10) - 1; // Month is 0-based
    else if (part === 'yyyy') dateInfo.year = parseInt(parts[index], 10);
  });

  return new Date(dateInfo.year, dateInfo.month, dateInfo.day);
}

// Helper function to format date to output format

function formatDate(date: Date, format: string) {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');

  const day = date.getDate().toString().padStart(2, '0');

  const year = date.getFullYear().toString();

  return format.replace('dd', day).replace('mm', month).replace('yyyy', year);
}

export function dateFormatter(inputDate: string, inputFormat: string, outputFormat: string) {
  // Parse input date using input format

  const parsedDate = parseDate(inputDate, inputFormat);

  // Format parsed date using output format

  const formattedDate = formatDate(parsedDate, outputFormat);

  return formattedDate;
}

export function convertToISODate(dateString: string, time: string = '00:00:00') {
  // Parse the date and time components

  const [timePart, period] = time.split(' ');

  const [year, month, day] = dateString.split('-').map(Number);

  let [hours, minutes] = timePart.split(':').map(Number);

  // Convert to 24-hour format if the period is "PM" and the hour is less than 12

  if (period === 'PM' && hours < 12) hours += 12;

  // Adjust for midnight case (12 AM)

  if (period === 'AM' && hours === 12) hours = 0;

  // Format as ISO 8601

  return new Date(
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
  );
}

/**
 *
 * @param deferredTime - time to defer the view in ms
 * @param isPlatformBrowser - use `isPlatformBrowser(inject(PLATFORM_ID))`
 * @param ngZone
 * @returns
 */

export const timerIfClient$ = (deferredTime: number, isPlatformBrowser: boolean) => {
  const ngZone = inject(NgZone);
  if (!isPlatformBrowser) {
    // On the server, immediately return an observable that completes.
    return of(false as true);
  }

  // If in the browser, run the outside-zone timeout.
  return outsideZoneTimer$(ngZone, deferredTime);
};

export function notUndefined<T>(t: T | undefined | null): t is T {
  return t !== undefined && t !== null;
}

export function isStringArray(array: unknown): array is string[] {
  return Array.isArray(array) && array.every(item => typeof item === 'string');
}

export type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

export function outsideZoneTimer$(ngZone: NgZone, deferTime: number): Observable<true> {
  // The Subject will emit once after the timeout, then complete.
  const subject = new Subject<true>();

  // Schedule the timeout outside Angular’s zone.
  ngZone.runOutsideAngular(() => {
    setTimeout(() => {
      // Re-enter the zone *only* if you need to trigger UI updates
      // or just emit from the subject outside the zone.
      subject.next(true);
      subject.complete();
    }, deferTime);
  });

  return subject.asObservable();
}
